import React, { memo } from "react";
import { useSelector } from "react-redux";
import { Router, Switch } from "react-router-dom";

import _ from "lodash";
import PropTypes from "prop-types";

import PrivateRoute from "components/PrivateRoute";
import SPRedirect from "components/SPRedirect";
import TasksDrawer from "components/TasksDrawer";
import AttachmentTypesMainView from "containers/AttachmentTypes/Main/AttachmentTypesMainView";
import AwardDetailView from "containers/Awards/Detail";
import AwardsMainView from "containers/Awards/Main";
import BudgetBuilder from "containers/BudgetBuilder/Detail/BudgetBuilderView";
import BudgetSettingsMainView from "containers/BudgetSettings/Main";
import CertificationTextMainView from "containers/CertificationText/Main/CertificationTextMainView";
import FormMainView from "containers/Form/Main/FormMainView";
import ModificationsMainView from "containers/Modifications/Main/ModificationsMainView";
import ProjectDetail from "containers/Project/Detail/ProjectDetailView";
import ProjectMainView from "containers/Project/Main";
import ProposalDetail from "containers/Proposal/Detail/ProposalDetailView";
import ProposalMainView from "containers/Proposal/Main";
import ReportingGridView from "containers/Reporting/Grid";
import { ResearchTeamRolesView } from "containers/ResearchTeamRolesView";
import ResolutionsMainView from "containers/Resolutions/Main/ResolutionsMainView";
import SubawardDetailView from "containers/Subaward/Detail";
import SubawardsMainView from "containers/Subaward/Main/SubawardsMainView";
import { TaskPage, MilestonePage } from "containers/Task";
import {
	ATTACHMENT_TYPES_BASE_ROUTE,
	BUDGET_SETTINGS_BASE_ROUTE,
	CERTIFICATION_TEXT_BASE_ROUTE,
	FORMS_BASE_ROUTE,
	MANAGE_RT_ROLES_ROUTE,
	MODIFICATIONS_BASE_ROUTE,
	PAGE_TITLES,
	RESOLUTIONS_BASE_ROUTE,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
} from "globals/constants";
import { useDictionary, useHasRoles } from "hooks";
import { tenantSelectors } from "store/selectors";
import { spUrl } from "utils";
import history from "utils/history";

const Routing = memo(({ isFormManager, children }) => {
	const dictionary = useDictionary("main");

	const { budgetBuilderEnabled } = useSelector(tenantSelectors.tenant);
	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
	} = useHasRoles([ ROLE_SP_SYSTEM_ADMINISTRATOR ]);

	const hasRouteAccess = isSystemAdmin || isFormManager;

	return (
		<Router history={history}>
			{children}

			<main id="content-main" aria-label={dictionary.landmarkAriaLabel || "Main content"}>
				<Switch>
					<PrivateRoute exact path={spUrl("/projects")} documentTitle={PAGE_TITLES.PROJECT_DASHBOARD} component={ProjectMainView}/>

					<PrivateRoute exact path={spUrl("/awards")} documentTitle={PAGE_TITLES.AWARDS_DASHBOARD} component={AwardsMainView}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId")} documentTitle={PAGE_TITLES.AWARD_DETAIL} component={AwardDetailView}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId/bb")} documentTitle={PAGE_TITLES.AWARD_DETAIL}  component={BudgetBuilder}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId/:modificationNumber")} documentTitle={PAGE_TITLES.AWARD_DETAIL} component={AwardDetailView}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId/task/create")} documentTitle={PAGE_TITLES.AWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/task/:taskId")} documentTitle={PAGE_TITLES.AWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/milestone/create")} documentTitle={PAGE_TITLES.AWARD_MILESTONE} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/milestone/:taskId")} documentTitle={PAGE_TITLES.AWARD_MILESTONE} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL} component={SubawardDetailView}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId/:modificationNumber")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL} component={SubawardDetailView}/>
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId/task/create")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId/task/:taskId")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId/milestone/create")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/awards/:awardId/sub-awards/:subawardId/milestone/:taskId")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={MilestonePage} />

					<PrivateRoute exact path={spUrl("/subawards")} documentTitle={PAGE_TITLES.SUBAWARDS_DASHBOARD} component={SubawardsMainView}/>
					<PrivateRoute exact path={spUrl("/subawards/:subawardId")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL} component={SubawardDetailView}/>
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/:modificationNumber")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL} component={SubawardDetailView}/>
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/bb")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL}  component={BudgetBuilder}/>
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/:modificationNumber")} documentTitle={PAGE_TITLES.SUBAWARDS_DETAIL} component={SubawardDetailView}/>
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/task/create")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/task/:taskId")} documentTitle={PAGE_TITLES.SUBAWARD_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/milestone/create")} documentTitle={PAGE_TITLES.SUBAWARD_MILESTONE} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/subawards/:subawardId/milestone/:taskId")} documentTitle={PAGE_TITLES.SUBAWARD_MILESTONE} component={MilestonePage} />

					<PrivateRoute exact path={spUrl("/proposals")} documentTitle={PAGE_TITLES.PROPOSAL_DASHBOARD} component={ProposalMainView}/>
					<PrivateRoute exact path={spUrl("/proposals/:proposalId")} documentTitle={PAGE_TITLES.PROPOSAL_DETAIL} component={ProposalDetail}/>
					<PrivateRoute exact path={spUrl("/proposals/:proposalId/task/create")} documentTitle={PAGE_TITLES.PROPOSAL_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/proposals/:proposalId/task/:taskId")} documentTitle={PAGE_TITLES.PROPOSAL_TASK} component={TaskPage} />
					<PrivateRoute exact path={spUrl("/proposals/:proposalId/milestone/create")} documentTitle={PAGE_TITLES.PROPOSAL_MILESTONE} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/proposals/:proposalId/milestone/:taskId")} documentTitle={PAGE_TITLES.PROPOSAL_MILESTONE} component={MilestonePage} />
					<PrivateRoute exact path={spUrl("/proposals/:proposalId/bb")} documentTitle={PAGE_TITLES.PROPOSAL_DETAIL}  component={BudgetBuilder}/>
					<PrivateRoute exact path={spUrl("/projects/:projectId")} documentTitle={PAGE_TITLES.PROJECT_DETAIL} component={ProjectDetail}/>
					<PrivateRoute exact path={spUrl("/projects/:projectId/task/create")} documentTitle={PAGE_TITLES.PROJECT_TASK} component={TaskPage}/>
					<PrivateRoute exact path={spUrl("/projects/:projectId/task/:taskId")} documentTitle={PAGE_TITLES.PROJECT_TASK} component={TaskPage}/>
					<PrivateRoute exact path={spUrl("/projects/:projectId/milestone/create")} documentTitle={PAGE_TITLES.PROJECT_MILESTONE} component={MilestonePage}/>
					<PrivateRoute exact path={spUrl("/projects/:projectId/milestone/:taskId")} documentTitle={PAGE_TITLES.PROJECT_MILESTONE} component={MilestonePage}/>

					<PrivateRoute exact path={spUrl("/reporting/awards")} documentTitle={PAGE_TITLES.REPORTING_AWARDS_DASHBOARD} component={ReportingGridView}/>
					<PrivateRoute exact path={spUrl("/reporting/subawards")} documentTitle={PAGE_TITLES.REPORTING_SUBAWARDS_DASHBOARD} component={ReportingGridView}/>
					<PrivateRoute exact path={spUrl("/reporting/proposals")} documentTitle={PAGE_TITLES.REPORTING_PROPOSALS_DASHBOARD} component={ReportingGridView}/>

					<PrivateRoute
						path={spUrl(FORMS_BASE_ROUTE)}
						documentTitle={PAGE_TITLES.FORMS}
						component={FormMainView}
						redirect={!hasRouteAccess}
					/>
					<PrivateRoute
						exact
						path={spUrl(RESOLUTIONS_BASE_ROUTE)}
						documentTitle={PAGE_TITLES.RESOLUTIONS}
						component={ResolutionsMainView}
						redirect={!isSystemAdmin}
					/>
					<PrivateRoute
						exact
						path={spUrl(MODIFICATIONS_BASE_ROUTE)}
						documentTitle={PAGE_TITLES.MODIFICATIONS}
						component={ModificationsMainView}
						redirect={!isSystemAdmin}
					/>
					<PrivateRoute
						exact
						path={spUrl(ATTACHMENT_TYPES_BASE_ROUTE)}
						documentTitle={PAGE_TITLES.ATTACHMENT_TYPES}
						component={AttachmentTypesMainView}
						redirect={!isSystemAdmin}
					/>
					{budgetBuilderEnabled && (
					 <PrivateRoute
						 exact
						 path={spUrl(BUDGET_SETTINGS_BASE_ROUTE)}
						 documentTitle={PAGE_TITLES.BUDGET_SETTINGS}
						 component={BudgetSettingsMainView}
						 redirect={!isSystemAdmin}
					 />
					)}
					<PrivateRoute
						exact
						path={spUrl(CERTIFICATION_TEXT_BASE_ROUTE)}
						documentTitle={PAGE_TITLES.CERTIFICATION_TEXT}
						component={CertificationTextMainView}
						redirect={!isSystemAdmin}
					/>

					<PrivateRoute
						exact
						path={spUrl(MANAGE_RT_ROLES_ROUTE)}
						documentTitle={PAGE_TITLES.MANAGE_RT_ROLES}
						component={ResearchTeamRolesView}
						redirect={!isSystemAdmin}
					/>

					<SPRedirect to={spUrl("/proposals")}/>
				</Switch>
				<TasksDrawer />
			</main>
		</Router>
	);
}, (prevProps, nextProps) => {
	return prevProps.isFormManager === nextProps.isFormManager
		|| _.isEqual(prevProps.children, nextProps.children);
});

Routing.propTypes = {
	children: PropTypes.node
};

export default Routing;
